import DupontLogger from "@utils/DupontLogger";

/**
 * Local/session storage based guard for login engagement calls.
 *
 * Behavior summary:
 * - Keep a per-user timestamp in localStorage.
 * - Treat a login session as active for LOGIN_SESSION_TIMEOUT_IN_MINUTES.
 * - Use a sessionStorage marker to detect a newly opened browser/tab session.
 * - On a new browser/tab session, clear prior user marker so first login can be captured.
 */
const LOGIN_SESSION_PREFIX = `wavepro_login_session_${import.meta.env.VITE_PROJECT_ENV}_`;
const LOGIN_SESSION_TIMEOUT_IN_MINUTES = 120;
const LOGIN_SESSION_TIMEOUT_IN_MS = LOGIN_SESSION_TIMEOUT_IN_MINUTES * 60 * 1000;

const Logger = DupontLogger("LoginEngagementSession");

const getLoginSessionKey = email => `${LOGIN_SESSION_PREFIX}${email.toLowerCase()}`;

const getParsedSession = email => {
  const sessionValue = localStorage.getItem(getLoginSessionKey(email));
  if (!sessionValue) {
    return null;
  }

  try {
    return JSON.parse(sessionValue);
  } catch (_error) {
    Logger.error("Failed to parse login session", { email, error: _error });
    return null;
  }
};

const hasActiveLoginSession = email => {
  const session = getParsedSession(email);
  if (!session?.timestamp) {
    return false;
  }

  return Date.now() - session.timestamp < LOGIN_SESSION_TIMEOUT_IN_MS;
};

/**
 * Decides whether login engagement should be sent for the user.
 *
 * Rules:
 * - Missing email: skip.
 * - Active session within timeout window: skip.
 * - Otherwise: allow engagement.
 *
 * @param {string} email - Logged-in user email.
 * @returns {boolean} True when login engagement should be sent.
 */
export const shouldSendLoginEngagement = email => {
  if (!email) {
    Logger.warn("Skipping login engagement: email is missing");
    updateLoginSessionTimestamp(email);
    return false;
  }

  const hasActiveSession = hasActiveLoginSession(email);
  updateLoginSessionTimestamp(email);

  if (hasActiveSession) {
    Logger.info("Skipping login engagement: active session exists within timeout", { email });
    return false;
  }

  Logger.info("Sending login engagement: no active session or session expired", { email });
  return true;
};

/**
 * Removes stored login session marker for a specific user.
 * Used on explicit logout.
 *
 * @param {string} email - Logged-in user email.
 * @returns {void}
 */
export const clearLoginSessionByEmail = email => {
  if (!email) {
    return;
  }

  localStorage.removeItem(getLoginSessionKey(email));
};

/**
 * Writes/refreshes the per-user login session timestamp in localStorage.
 *
 * @param {string} email - Logged-in user email.
 * @returns {void}
 */
export const updateLoginSessionTimestamp = email => {
  if (!email) {
    return;
  }
  const key = getLoginSessionKey(email);
  const value = JSON.stringify({ email, timestamp: Date.now() });
  localStorage.setItem(key, value);
};

/**
 * Clears all WavePro login session markers for the current environment.
 *
 * @returns {void}
 */
export const clearAllLoginSessions = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(LOGIN_SESSION_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};
